import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import themeContext from "../../../../context/theme/ThemeContext";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import Tool from "../Tools/ToolComponents/Tool";
import RotateRight from "../../../../components/SVGComponents/Tools/RotateRight";
import FreeRotate from "../../../../components/SVGComponents/Tools/FreeRotate";
import DropDown from "../../../../components/SVGComponents/Tools/DropDown";
import { toolStyle } from "../../styles/styles";
import GetToolSVG from "../Tools/ToolComponents/GetToolSVG";
import { resetRotation } from "../../helpers/CornerstoneViewportService";

// Rotate/flip/align group for the mobile bar: a single "Rotate" button that opens an
// upward dropdown. Each row reuses <Tool> so the viewport actions are the shared, tested ones.
//  added Align Left/Center/Right (present in the desktop Rotate menu).
const ROTATE_TOOLS = [
  { key: "RotateRight", NotFor: [], name: "viewer.tools.rotate-right" },
  { key: "RotateLeft", NotFor: [], name: "viewer.tools.rotate-left" },
  { key: "FlipVertical", NotFor: [], name: "viewer.tools.flipV" },
  { key: "FlipHorizontal", NotFor: [], name: "viewer.tools.flipH" },
  { key: "AlignLeft", NotFor: [], name: "viewer.tools.align-left" },
  { key: "AlignCenter", NotFor: [], name: "viewer.tools.align-center" },
  { key: "AlignRight", NotFor: [], name: "viewer.tools.align-right" },
  { key: "LockLeft", NotFor: [], name: "viewer.tools.lock-left" },
  { key: "LockRight", NotFor: [], name: "viewer.tools.lock-right" },
];

const MobileRotateMenu = () => {
  const { theme } = useContext(themeContext);
  const { t } = useTranslation();
  //  needed to activate the drag-to-rotate "FreeRotate" tool from the menu.
  //activeViewportIndex is needed for Clear Rotation (resetRotation).
  const {
    activeTool,
    setActiveTool,
    setPreviousActiveTool,
    handleDefaultToolChange,
    activeViewportIndex,
  } = useContext(ViewportContext);
  const [showMenu, setShowMenu] = useState(false);
  // Bottom toolbar -> dropdown opens upward (anchor bottom edge above the button).
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const toggleMenu = () => {
    setShowMenu((prev) => {
      const next = !prev;
      if (next && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPos({
          bottom: window.innerHeight - rect.top + 6,
          left: rect.left,
        });
      }
      return next;
    });
  };

  return (
    <div
      ref={menuRef}
      style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div ref={buttonRef} onClick={toggleMenu} style={{ ...toolStyle }}>
        <div
          style={{
            height: "65%",
            width: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          <div className="highlight-tool-mobile">
            <RotateRight
              color={showMenu ? theme.palette.activeTool : theme.palette.inactiveTool}
            />
          </div>
          <DropDown
            up
            color={showMenu ? theme.palette.activeTool : theme.palette.inactiveTool}
          />
        </div>
        <span
          style={{
            fontSize: "9px",
            lineHeight: "100%",
            fontWeight: "400",
            color: "var(--mobile-color-txt)",
            display: "block",
            width: "60px",
            textAlign: "center",
          }}
        >
          {t("viewer.tools.rotate")}
        </span>
      </div>

      {showMenu && (
        <div
          className="viewer-tool-more-dropdown"
          style={{
            position: "fixed",
            bottom: menuPos.bottom,
            left: menuPos.left,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "4px",
            borderRadius: "5px",
            padding: "8px 6px",
            // the menu now has more items (rotate/flip/align/clear) — cap
            // its height and scroll so it never overflows off the top of a small screen.
            maxHeight: "70vh",
            overflowY: "auto",
            boxShadow:
              "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
          }}
          onClick={() => setShowMenu(false)}
        >
          {/*  drag-to-rotate ("Rotate") — activates the FreeRotate tool so the
              user can drag the image to rotate like the desktop viewer. Previously the mobile menu
              only had the one-shot rotate/flip actions below, so there was no drag-rotate. */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(false);
              setPreviousActiveTool(activeTool);
              if (activeTool === "FreeRotate") {
                handleDefaultToolChange(); // toggle off if already active
              } else {
                setActiveTool("FreeRotate");
              }
            }}
            style={{ ...toolStyle }}
          >
            <div
              style={{
                height: "65%",
                width: "60px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              <div className="highlight-tool-mobile">
                <FreeRotate
                  color={activeTool === "FreeRotate" ? theme.palette.activeTool : theme.palette.inactiveTool}
                />
              </div>
            </div>
            <span
              style={{
                fontSize: "9px",
                lineHeight: "100%",
                fontWeight: "400",
                color: "var(--mobile-color-txt)",
                display: "block",
                width: "60px",
                textAlign: "center",
              }}
            >
              {t("viewer.tools.free-rotate")}
            </span>
          </div>

          {/* Separator between drag-rotate and the one-shot rotate/flip actions */}
          <div style={{ borderTop: "1px solid #3B3A39", alignSelf: "stretch", margin: "2px 0" }} />

          {ROTATE_TOOLS.map((tool, index) => (
            <React.Fragment key={index}>
              {/*  divider between the rotate/flip tools and the align tools,
                  matching the divider above (between drag-rotate and rotate/flip). */}
              {tool.key === "AlignLeft" && (
                <div style={{ borderTop: "1px solid #3B3A39", alignSelf: "stretch", margin: "2px 0" }} />
              )}
              {/* And again between aligning and locking that alignment. */}
              {tool.key === "LockLeft" && (
                <div style={{ borderTop: "1px solid #3B3A39", alignSelf: "stretch", margin: "2px 0" }} />
              )}
              <Tool tool={tool} />
            </React.Fragment>
          ))}

          {/* Separator before Clear Rotation */}
          <div style={{ borderTop: "1px solid #3B3A39", alignSelf: "stretch", margin: "2px 0" }} />

          {/*  Clear Rotation — clears rotation/flip movement only
              (resetRotation), matching the desktop Rotate menu. NOT a full viewport reset. */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              resetRotation(activeViewportIndex);
              setShowMenu(false);
            }}
            style={{ ...toolStyle }}
          >
            <div
              style={{
                height: "65%",
                width: "60px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              <div className="highlight-tool-mobile">
                <GetToolSVG toolName="Reset" hovered={false} />
              </div>
            </div>
            <span
              style={{
                fontSize: "9px",
                lineHeight: "100%",
                fontWeight: "400",
                color: "var(--mobile-color-txt)",
                display: "block",
                width: "60px",
                textAlign: "center",
              }}
            >
              {t("viewer.tools.clear-rotation")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileRotateMenu;
